<script setup>
import { computed, inject, onMounted, onUnmounted, ref } from "vue";
import { useEditableElementStore } from "@/views/page/store/editable-element.store";
import { ButtonTertiaryIconXs } from "@easy/ui/buttons";
import { MagicWandIcon, PencilSquareIcon, PreloaderIcon, VisibleIcon } from "@easy/ui/icons";
import { useEditorActions } from "@/views/tools/page/utils/useEditorActions";
import { useMagicFieldExchangeStore } from "@/apps/page/services/magic-field-exchange.store";
import { useEditableElementBounds } from "@/views/page/useEditableElementBounds";
import AiGenerationSkeleton from "@/shared/components/AiGenerationSkeleton.vue";

const props = defineProps({
    fieldName: {
        type: String,
        required: true,
    },
    blockId: {
        type: Number,
        //required: true,
    },
    hideable: {
        type: Boolean,
        default: true,
    },
    withPadding: {
        type: Boolean,
        default: true,
    },
    onSelect: {
        type: [Function, undefined],
    },
});

const { editBlockElement, hideBlockElement, magicallyExchangeContent } = useEditorActions();
const { isBlockFieldExchanging } = useMagicFieldExchangeStore();

const isMinimap = inject("isMinimap");
const isPreview = inject("isPreview");
const isDragging = inject("isDragging");

const enabled = isPreview;

const isHovered = ref(false);
const contentRef = ref(null);

const editableElementStore = useEditableElementStore();

const { bounds } = useEditableElementBounds(contentRef);

const isSelected = computed(
    () =>
        editableElementStore.selectedElement &&
        editableElementStore.selectedElement.fieldName === props.fieldName &&
        editableElementStore.selectedElement.blockId === props.blockId
);

const showBorder = computed(() => (isHovered.value || isSelected.value) && !isMinimap);

const isMagicExchanging = computed(() => isBlockFieldExchanging(props.blockId, props.fieldName));

function handleSelect() {
    if (isMinimap) return;

    editableElementStore.selectElement({ fieldName: props.fieldName, blockId: props.blockId });
    editBlockElement(props.blockId, props.fieldName);

    props.onSelect?.();
}

function handleClickOutside(event) {
    if (!event.target.closest(".editable-element")) {
        editableElementStore.clearSelection();
    }
}

onMounted(() => {
    if (!contentRef.value) return;

    document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside);
});

function edit() {
    editBlockElement(props.blockId, props.fieldName);

    if (
        // Ensure selectedElement is present [EAS-2466]
        editableElementStore.selectedElement &&
        (editableElementStore.selectedElement.fieldName !== props.fieldName ||
            editableElementStore.selectedElement.blockId !== props.blockId)
    ) {
        editableElementStore.selectElement({ fieldName: props.fieldName, blockId: props.blockId });
    }

    props.onSelect?.();
}

function doMagic() {
    magicallyExchangeContent(props.blockId, props.fieldName);
}

function toggleHide() {
    hideBlockElement(props.blockId, props.fieldName);
}

const isToolbarOverflowing = computed(() => {
    if (!bounds.value) return false;

    return bounds.value.top - 56 < 0;
});

const toolbarPosition = computed(() => {
    if (!bounds.value) return {};

    return isToolbarOverflowing.value
        ? {
              left: `${bounds.value.left - 8}px`,
              top: `${bounds.value.top + bounds.value.height}px`,
          }
        : {
              left: `${bounds.value.left - 8}px`,
              top: `${bounds.value.top - (props.withPadding ? 56 : 48)}px`,
          };
});
</script>

<template>
    <template v-if="enabled && !isMinimap && !isDragging">
        <div ref="contentRef" class="editable-element contents">
            <!-- todo: repair preloader (better UIx) -->
            <slot></slot>

            <Teleport to="body" v-if="contentRef && bounds">
                <div
                    v-if="showBorder && !isSelected"
                    class="fixed h-[50px] z-[101] flex flex-col"
                    :class="{ 'justify-end': isToolbarOverflowing }"
                    :style="toolbarPosition"
                    @mouseenter="isHovered = true"
                    @mouseleave="isHovered = false"
                >
                    <div
                        class="flex gap-1 border ct-border-1 rounded-lg px-2 py-1 shadow-md ct-bg-1 h-[38px]"
                    >
                        <ButtonTertiaryIconXs @click.stop="edit">
                            <PencilSquareIcon />
                        </ButtonTertiaryIconXs>
                        <ButtonTertiaryIconXs @click.stop="doMagic" :disabled="isMagicExchanging">
                            <PreloaderIcon v-if="isMagicExchanging" />
                            <MagicWandIcon v-else />
                        </ButtonTertiaryIconXs>
                        <ButtonTertiaryIconXs v-if="hideable" @click.stop="toggleHide">
                            <VisibleIcon />
                        </ButtonTertiaryIconXs>
                    </div>
                </div>

                <div
                    v-if="enabled"
                    class="fixed bg-transparent rounded-[4px] z-[100]"
                    :style="{
                        left: `${bounds.left - (withPadding ? 8 : 0)}px`,
                        top: `${bounds.top - (withPadding ? 8 : 0)}px`,
                        width: `${bounds.width + (withPadding ? 16 : 0)}px`,
                        height: `${bounds.height + (withPadding ? 16 : 0)}px`,
                    }"
                    :class="[
                        showBorder ? 'border-2 border-blue-500' : 'border-2 border-transparent',
                    ]"
                    @mouseenter="isHovered = true"
                    @mouseleave="isHovered = false"
                    @click.stop="handleSelect"
                >
                    <!-- magic exchanging skeleton... -->
                    <AiGenerationSkeleton v-if="isMagicExchanging" />
                </div>
            </Teleport>
        </div>
    </template>
    <template v-else>
        <slot></slot>
    </template>
</template>
